import { getStoredRealm } from "@/utils/auth-storage";
import { Button } from "@mui/material";
import {
  AudioChangeAction,
  DialOutOption,
  DialoutState,
  MobileVideoFacingMode,
  RecordingStatus,
  SharePrivilege,
  ShareStatus,
  VideoCapturingState,
} from "@zoom/videosdk";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ZoomMediaContext } from "../../ZoomMediaProvider";
import Flex from "../../components/Flex";
import { useRoleController } from "../../components/useRoleController";
import useDisclosure, { DisclosureHost } from "../../hooks/use-disclosure";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useUpdateAppointmentStatus } from "../../queries/use-change-appointment-status";
import { closeAndResetJoinRoom } from "../../redux/feature/join-room/JoinRoomSlice";
import { RootState, store } from "../../redux/store";
import ParticipantsListDrawer from "../ParticipantsList";
import Settings from "../Settings";
import { useMount, useUnmount } from "../hooks";
import { useScheduleNoShow } from "../hooks/useSheduleNoShow";
import { isAndroidOrIOSBrowser } from "../platform";
import { getPhoneCallStatusDescription } from "../video-constants";
import { MediaDevice } from "../video-types";
import { NoShowConfirmation } from "../video/components/NoShow";
import {
  RecordButtonProps,
  RecordingButton,
  getRecordingButtons,
} from "../video/components/recording";
import IsoRecordingModal from "../video/components/recording-ask-modal";
import {
  CAMERA_START_FAILED_MESSAGE,
  MICROPHONE_START_FAILED_MESSAGE,
  START_AUDIO_OPTIONS,
} from "../zoom-constant";
import { ZoomContext } from "../zoom-context";
import { getStartVideoOptions } from "../zoom-utils";
import JoinRoomCameraButton from "./JoinRoomCameraButton";
import JoinRoomMicroPhoneButton from "./JoinRoomMicroPhoneButton";
import { LeaveButton } from "./Leave";
import { ScreenShareButton } from "./ScreenShare";
import "./video-footer.scss";

interface VideoFooterProps {
  className?: string;
  selfShareCanvas?: HTMLCanvasElement | HTMLVideoElement | null;
  sharing?: boolean;
  hasPatientAttendedCall: boolean;
  disclosureHost: DisclosureHost;
}

const isAudioEnable = typeof AudioWorklet === "function";

const VideoFooter = (props: VideoFooterProps) => {
  const { selfShareCanvas, sharing } = props;
  const zmClient = useContext(ZoomContext);
  if (!zmClient) {
    throw new Error("zmClient is not available. ");
  }
  const mediaContext = useContext(ZoomMediaContext);

  if (!mediaContext) {
    throw new Error("ZoomMediaContext is not available.");
  }

  const {
    mediaStream,
    activeCamera,
    activeMicrophone,
    activeSpeaker,
    setActiveMicrophone,
    setActiveSpeaker,
    setActiveCamera,
    setIsStartedVideo: setContextIsStartedVideo,
    setIsStartedAudio: setContextIsStartedAudio,
    setIsAudioMuted: setContextIsAudioMuted,
  } = mediaContext;
  const showSnackbar = useSnackbar();
  const recordingClient = zmClient?.getRecordingClient();
  /* Seeded from the preview screen, which already started the session media
     with the user's camera/microphone choices before this footer mounted. */
  const [isStartedAudio, setIsStartedAudio] = useState(
    () => mediaContext.isStartedAudio
  );

  const [isStartedVideo, setIsStartedVideo] = useState(
    () => mediaContext.isStartedVideo
  );
  const [audio, setAudio] = useState(zmClient?.getCurrentUserInfo()?.audio);
  const [isSupportPhone, setIsSupportPhone] = useState(false);
  const [phoneCountryList, setPhoneCountryList] = useState<any[]>([]);
  const [phoneCallStatus, setPhoneCallStatus] = useState<DialoutState>();
  const [isBlur] = useState(false);
  const [isMuted, setIsMuted] = useState(() => mediaContext.isAudioMuted);
  // const [isMuted, setIsMuted] = useState(true);

  const [micList, setMicList] = useState<MediaDevice[]>(
    mediaStream?.getMicList() ?? []
  );
  const [speakerList, setSpeakerList] = useState<MediaDevice[]>(
    mediaStream?.getSpeakerList() ?? []
  );
  const [cameraList, setCameraList] = useState<MediaDevice[]>(
    mediaStream?.getCameraList() ?? []
  );
  const [isComputerAudioDisabled, setIsComputerAudioDisabled] = useState(false);
  const [sharePrivilege, setSharePrivilege] = useState(SharePrivilege.Unlocked);
  const [activePlaybackUrl] = useState("");
  const [isMicrophoneForbidden, setIsMicrophoneForbidden] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState<"" | RecordingStatus>(
    recordingClient?.getCloudRecordingStatus() || ""
  );
  const [selectedBackground, setSelectedBackground] = useState<string>("");
  const patientListDisclosure = useDisclosure();
  const settingsDisclosure = useDisclosure();
  const [recordingIsoStatus, setRecordingIsoStatus] = useState<
    "" | RecordingStatus
  >("");
  const navigate = useNavigate();
  // const ref = useRef(searchParams.get("appointmentid"));
  const location = useLocation();
  const locationRef = useRef(location.pathname);

  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location]);

  const { mutateAsync: updateStatus } = useUpdateAppointmentStatus();

  const NoShowDisclosure = useDisclosure(false);

  const [isTrigger, setIsTrigger] = useState(false);
  const { status, disableScheduler: disabledNoShowScheduler } =
    useScheduleNoShow({
      scheduleTime: 1800000,
      onSchedule: () => {
        setIsTrigger(true);
      },
    });

  useEffect(() => {
    if (isTrigger) {
      if (zmClient?.isHost() && !props?.hasPatientAttendedCall) {
        NoShowDisclosure.open();
      }
    }
  }, [isTrigger]);

  const onCameraClick = useCallback(async () => {
    try {
      if (isStartedVideo) {
        await mediaStream?.stopVideo();
        setIsStartedVideo(false);
        setContextIsStartedVideo(false);
      } else {
        await mediaStream?.startVideo(
          getStartVideoOptions(mediaStream, activeCamera, selectedBackground)
        );
        setIsStartedVideo(true);
        setContextIsStartedVideo(true);
      }
    } catch (error) {
      showSnackbar({ type: "error", message: CAMERA_START_FAILED_MESSAGE });
    }
  }, [mediaStream, isStartedVideo, isBlur, activeCamera, selectedBackground]);
  //FUTURE REFERENCE
  // use : auto start audio
  // useEffect(() => {
  //   (async () => {
  //     await mediaStream
  //       ?.startAudio({
  //         highBitrate: true,
  //         mute: !isAStarted,
  //       })
  //       .then((res) => {
  //         console.log("val here res", res);
  //       })
  //       .catch((e) => {
  //         console.log("val here exception", e);
  //       });
  //     // await mediaStream?.startAudio({ highBitrate: true, mute: true });
  //     console.log("Audio started");
  //     setIsStartedAudio(true);
  //     // setIsMuted(true);
  //   })();
  //   if (isVStarted)
  //     (async () => {
  //       const startVideoOptions = {
  //         hd: true,
  //         fullHd: true,
  //         ptz: mediaStream?.isBrowserSupportPTZ(),
  //         originalRatio: true,
  //       };

  //       if (mediaStream?.isSupportVirtualBackground() && selectedBackground) {
  //         Object.assign(startVideoOptions, {
  //           virtualBackground: {
  //             imageUrl: selectedBackground,
  //           },
  //         });
  //       }
  //       await mediaStream?.startVideo(startVideoOptions);
  //       setIsStartedVideo(true);
  //     })();
  //   // return async () => {
  //   //   await mediaStream?.stopAudio();
  //   // };
  // }, [isAStarted]);

  const onMicrophoneClick = useCallback(async () => {
    try {
      if (!isStartedAudio) {
        await mediaStream?.startAudio(
          activePlaybackUrl
            ? { mediaFile: { url: activePlaybackUrl, loop: true } }
            : {
                ...START_AUDIO_OPTIONS,
                ...(activeMicrophone ? { microphoneId: activeMicrophone } : {}),
                ...(activeSpeaker ? { speakerId: activeSpeaker } : {}),
              }
        );
        setIsStartedAudio(true);
        setContextIsStartedAudio(true);
        setIsMuted(false);
        setContextIsAudioMuted(false);
        return;
      }

      if (isMuted) {
        await mediaStream?.unmuteAudio();
        setIsMuted(false);
        setContextIsAudioMuted(false);
      } else {
        await mediaStream?.muteAudio();
        setIsMuted(true);
        setContextIsAudioMuted(true);
      }
    } catch (e: any) {
      if (
        e?.type === "INSUFFICIENT_PRIVILEGES" &&
        e?.reason === "USER_FORBIDDEN_MICROPHONE"
      ) {
        setIsMicrophoneForbidden(true);
      } else {
        showSnackbar({
          type: "error",
          message: MICROPHONE_START_FAILED_MESSAGE,
        });
      }
    }
  }, [
    mediaStream,
    isStartedAudio,
    isMuted,
    activePlaybackUrl,
    activeMicrophone,
    activeSpeaker,
  ]);

  const onPhoneCall = async (
    code: string,
    phoneNumber: string,
    name: string,
    option: DialOutOption
  ) => {
    await mediaStream?.inviteByPhone(code, phoneNumber, name, option);
  };

  const onPhoneCallCancel = async (
    code: string,
    phoneNumber: string,
    option: { callMe: boolean }
  ) => {
    if (
      [
        DialoutState.Calling,
        DialoutState.Ringing,
        DialoutState.Accepted,
      ].includes(phoneCallStatus as any)
    ) {
      await mediaStream?.cancelInviteByPhone(code, phoneNumber, option);
      await new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
        }, 3000);
      });
    }
    return Promise.resolve();
  };

  const onHostAudioMuted = useCallback(
    (payload: any) => {
      const { action, type } = payload;
      if (action === AudioChangeAction.Join) {
        setIsStartedAudio(true);
        setContextIsStartedAudio(true);
        setAudio(type);
        setTimeout(() => {
          setIsMuted(!!zmClient.getCurrentUserInfo()?.muted);
          setContextIsAudioMuted(!!zmClient.getCurrentUserInfo()?.muted);
        }, 1000);
      } else if (action === AudioChangeAction.Leave) {
        setIsStartedAudio(false);
        setContextIsStartedAudio(false);
      } else if (action === AudioChangeAction.Muted) {
        setIsMuted(true);
        setContextIsAudioMuted(true);
        // if (source === MutedSource.PassiveByMuteOne) {
        // }
      } else if (action === AudioChangeAction.Unmuted) {
        setIsMuted(false);
        setContextIsAudioMuted(false);
        // if (source === "passive") {
        // }
      }
    },
    [zmClient]
  );

  const onScreenShareClick = useCallback(async () => {
    if (mediaStream?.getShareStatus() === ShareStatus.End && selfShareCanvas) {
      await mediaStream?.startShareScreen(selfShareCanvas, {
        requestReadReceipt: true,
      });
    }
  }, [mediaStream, selfShareCanvas]);

  // const onLeaveClick = useCallback(async () => {
  //   console.log("Temp", locationRef.current);

  //   await zmClient?.leave().then(() => {
  //     store.dispatch(closeAndResetJoinRoom());
  //   });

  //   if (
  //     !store.getState().joinRoom.isMinimize &&
  //     locationRef.current === "/app/preview"
  //   ) {
  //     console.log("navigating to Previous Path");

  //     navigate(-1);
  //   }
  // }, [zmClient]);

  async function onLeaveClick() {
    await zmClient?.leave().then(() => {
      store.dispatch(closeAndResetJoinRoom());
    });
    if (
      !store.getState().joinRoom.isMinimize &&
      locationRef.current === "/app/preview"
    ) {
      navigate(-1);
    }
  }

  const onPassivelyStopShare = useCallback(() => {}, []);

  const onDeviceChange = useCallback(() => {
    if (mediaStream) {
      setMicList(mediaStream.getMicList());
      setSpeakerList(mediaStream.getSpeakerList());
      if (!isAndroidOrIOSBrowser()) {
        setCameraList(mediaStream.getCameraList());
      }
      setActiveMicrophone(mediaStream.getActiveMicrophone());
      setActiveSpeaker(mediaStream.getActiveSpeaker());
      setActiveCamera(mediaStream.getActiveCamera());
    }
  }, [mediaStream]);

  // console.log("cameraList ", cameraList);
  // console.log("microhoineList", micList);
  // console.log(mediaStream?.getSpeakerList());

  const onRecordingChange = useCallback(() => {
    setRecordingStatus(recordingClient?.getCloudRecordingStatus() || "");
  }, [recordingClient]);

  const onRecordingISOChange = useCallback(
    (payload: any) => {
      if (
        payload?.userId === zmClient?.getSessionInfo().userId ||
        payload?.status === RecordingStatus.Ask
      ) {
        setRecordingIsoStatus(payload?.status);
      }
    },
    [zmClient]
  );

  const onDialOutChange = useCallback((payload: any) => {
    setPhoneCallStatus(payload.code);
  }, []);

  const onRecordingClick = async (key: string) => {
    switch (key) {
      case "Record": {
        await recordingClient?.startCloudRecording();
        break;
      }
      case "Resume": {
        await recordingClient?.resumeCloudRecording();
        break;
      }
      case "Stop": {
        await recordingClient?.stopCloudRecording();
        break;
      }
      case "Pause": {
        await recordingClient?.pauseCloudRecording();
        break;
      }
      case "Status": {
        break;
      }
      default: {
        await recordingClient?.startCloudRecording();
      }
    }
  };

  const onVideoCaptureChange = useCallback((payload: any) => {
    const isCapturing = payload.state === VideoCapturingState.Started;
    setIsStartedVideo(isCapturing);
    setContextIsStartedVideo(isCapturing);
  }, []);

  const onShareAudioChange = useCallback(
    (payload: any) => {
      const { state } = payload;
      if (state === "on") {
        if (!mediaStream?.isSupportMicrophoneAndShareAudioSimultaneously()) {
          setIsComputerAudioDisabled(true);
        }
      } else if (state === "off") {
        setIsComputerAudioDisabled(false);
      }
    },
    [mediaStream]
  );

  const onHostAskToUnmute = useCallback(() => {
    // const { reason } = payload;
  }, []);

  const onCaptionStatusChange = useCallback(() => {
    // const { autoCaption } = payload;
    // if (autoCaption) {
    // }
  }, []);

  const onCanSeeMyScreen = useCallback(() => {}, []);

  useEffect(() => {
    zmClient?.on("current-audio-change", onHostAudioMuted);
    zmClient?.on("passively-stop-share", onPassivelyStopShare);
    zmClient?.on("device-change", onDeviceChange);
    zmClient?.on("recording-change", onRecordingChange);
    zmClient?.on("individual-recording-change", onRecordingISOChange);
    zmClient?.on("dialout-state-change", onDialOutChange);
    zmClient?.on("video-capturing-change", onVideoCaptureChange);
    zmClient?.on("share-audio-change", onShareAudioChange);
    zmClient?.on("host-ask-unmute-audio", onHostAskToUnmute);
    zmClient?.on("caption-status", onCaptionStatusChange);
    zmClient?.on("share-can-see-screen", onCanSeeMyScreen);
    return () => {
      zmClient?.off("current-audio-change", onHostAudioMuted);
      zmClient?.off("passively-stop-share", onPassivelyStopShare);
      zmClient?.off("device-change", onDeviceChange);
      zmClient?.off("recording-change", onRecordingChange);
      zmClient?.off("individual-recording-change", onRecordingISOChange);
      zmClient?.off("dialout-state-change", onDialOutChange);
      zmClient?.off("video-capturing-change", onVideoCaptureChange);
      zmClient?.off("share-audio-change", onShareAudioChange);
      zmClient?.off("host-ask-unmute-audio", onHostAskToUnmute);
      zmClient?.off("caption-status", onCaptionStatusChange);
      zmClient?.off("share-can-see-screen", onCanSeeMyScreen);
    };
  }, [
    zmClient,
    onHostAudioMuted,
    onPassivelyStopShare,
    onDeviceChange,
    onRecordingChange,
    onDialOutChange,
    onVideoCaptureChange,
    onShareAudioChange,
    onHostAskToUnmute,
    onCaptionStatusChange,
    onCanSeeMyScreen,
    onRecordingISOChange,
  ]);

  useUnmount(() => {
    // Camera and microphone belong to the session, not to this footer: a
    // remount (minimize, layout switch) must not tear them down. Leaving the
    // call goes through `onLeaveClick`.
    if (zmClient?.getSessionInfo().isInMeeting) {
      mediaStream?.stopShareScreen();
    }
  });

  // useMount(() => {
  //   mountDevices().then((devices) => {
  //     setMicList(devices.mics);
  //     setCameraList(devices.cameras);
  //     setSpeakerList(devices.speakers);
  //     if (devices.speakers.length > 0) {
  //       setActiveSpeaker(devices.speakers[0].deviceId);
  //     }
  //     if (devices.mics.length > 0) {
  //       setActiveMicrophone(devices.mics[0].deviceId);
  //     }
  //     if (devices.cameras?.length > 0) {
  //       setActiveCamera(devices.cameras[0].deviceId);
  //     }
  //   });
  // });

  useMount(() => {
    if (mediaStream) {
      setIsSupportPhone(!!mediaStream.isSupportPhoneFeature());
      setPhoneCountryList(mediaStream.getSupportCountryInfo() || []);
      setSharePrivilege(mediaStream.getSharePrivilege());
      if (isAndroidOrIOSBrowser()) {
        setCameraList([
          { deviceId: MobileVideoFacingMode.User, label: "Front-facing" },
          { deviceId: MobileVideoFacingMode.Environment, label: "Rear-facing" },
        ]);
      }
    }
  });

  const recordingButtons: RecordButtonProps[] = getRecordingButtons(
    recordingStatus,
    zmClient?.isHost()
  );

  const isMinimizeCall = useSelector(
    (state: RootState) => state.joinRoom.isMinimize
  );
  const { hasUpdate: hasUpdateRecordMeeting } =
    useRoleController("Record Meeting");

  return (
    <Flex
      mt={"4px"}
      alignItems={"center"}
      justifyContent={"center"}
      borderRadius={isMinimizeCall ? 16 : undefined}
      justifySelf={"center"}
      height={isMinimizeCall ? "55px" : undefined}
      width={isMinimizeCall ? "380px" : undefined}
      bgcolor={isMinimizeCall ? "gray" : undefined}
      left={isMinimizeCall ? "50%" : undefined}
      bottom={isMinimizeCall ? "10px" : undefined}
      sx={
        isMinimizeCall
          ? {
              transform: "translateX(-50%)",
            }
          : undefined
      }
      position={isMinimizeCall ? "absolute" : undefined}
      zIndex={999}
      className={!isMinimizeCall ? "video-footer-container" : undefined}
    >
      <div className={"video-footer"}>
        {isAudioEnable && (
          <JoinRoomMicroPhoneButton
            isStartedAudio={isStartedAudio}
            isMuted={isMuted}
            isSupportPhone={isSupportPhone}
            audio={audio}
            phoneCountryList={phoneCountryList}
            onPhoneCallClick={onPhoneCall}
            onPhoneCallCancel={onPhoneCallCancel}
            phoneCallStatus={getPhoneCallStatusDescription(phoneCallStatus)}
            onMicrophoneClick={onMicrophoneClick}
            microphoneList={micList}
            speakerList={speakerList}
            activeMicrophone={activeMicrophone as any}
            activeSpeaker={activeSpeaker as any}
            disabled={isComputerAudioDisabled}
            isMicrophoneForbidden={isMicrophoneForbidden}
          />
        )}

        <JoinRoomCameraButton
          activeCamera={activeCamera}
          cameraList={cameraList}
          isStartedVideo={isStartedVideo}
          onCameraClick={onCameraClick}
        />

        {sharing && (
          <ScreenShareButton
            sharePrivilege={sharePrivilege}
            isHostOrManager={zmClient?.isHost() || zmClient?.isManager()}
            onScreenShareClick={onScreenShareClick}
            onSharePrivilegeClick={async (privilege) => {
              await mediaStream?.setSharePrivilege(privilege);
              setSharePrivilege(privilege);
            }}
          />
        )}
        {!zmClient?.getCurrentUserInfo()?.subsessionId &&
          hasUpdateRecordMeeting &&
          recordingButtons.map((button: RecordButtonProps) => {
            return (
              <RecordingButton
                key={button.text}
                onClick={() => {
                  onRecordingClick(button.text);
                }}
                {...button}
              />
            );
          })}

        <LeaveButton
          onLeaveClick={() => {
            disabledNoShowScheduler();

            if (zmClient?.isHost() && !props.hasPatientAttendedCall)
              NoShowDisclosure.open();
            else onLeaveClick();
          }}
        />
        <Settings
          isMuted={isMuted}
          videoStarted={isStartedVideo}
          isStartedAudio={isStartedAudio}
          selectedBackground={selectedBackground}
          setSelectedBackground={setSelectedBackground}
          disclosureHost={settingsDisclosure}
          onCameraClick={onCameraClick}
        />
        <NoShowConfirmation
          disclosureHost={NoShowDisclosure}
          onCancel={() => {
            NoShowDisclosure.close();

            if (status !== "scheduled") {
              onLeaveClick();
            }

            disabledNoShowScheduler();
          }}
          onConfirm={() => {
            return updateStatus({
              reason: "",
              appointmentId:
                store.getState().joinRoom.appointmentId?.toString() ?? "",
              status: "NO_SHOW",
              xTENANTID: getStoredRealm() ?? undefined,
            }).then(() => {
              NoShowDisclosure.close();
              onLeaveClick();
              // openSuccessModal();
              // setSuccessMessage(res.data?.message as any);
            });
          }}
        />
        <ParticipantsListDrawer disclosureHost={patientListDisclosure} />

        {recordingIsoStatus === RecordingStatus.Ask && (
          <IsoRecordingModal
            onClick={() => recordingClient?.acceptIndividualRecording()}
            onCancel={() => recordingClient?.declineIndividualRecording()}
          />
        )}
        {/* {!mediaStream?.isSupportVirtualBackground() && (
          <VideoMaskModel
            visible={videoMaskVisible}
            setVisible={setVideoMaskVisible}
            isMirrored={isMirrored}
          />
        )} */}
      </div>

      {!isMinimizeCall && (
        <div className="right-buttons-container">
          <Button
            id="addPatient"
            onClick={() => patientListDisclosure.open()}
            endIcon={
              <svg
                id="Component_2279_1"
                data-name="Component 2279 – 1"
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 40 40"
              >
                <g
                  id="Group_93495"
                  data-name="Group 93495"
                  transform="translate(-10180 4581)"
                >
                  <g id="Group_93494" data-name="Group 93494">
                    <g
                      id="Ellipse_1315"
                      data-name="Ellipse 1315"
                      transform="translate(10180 -4581)"
                      fill={isMinimizeCall ? "#fff" : "gray"}
                      stroke="#aaa"
                      stroke-width="1"
                      opacity="0"
                    >
                      <circle cx="20" cy="20" r="20" stroke="none" />
                      <circle cx="20" cy="20" r="19.5" fill="none" />
                    </g>
                    <g
                      id="person_add_black_24dp"
                      transform="translate(10188 -4573)"
                    >
                      <path
                        id="Path_206776"
                        data-name="Path 206776"
                        d="M0,0H24V24H0Z"
                        fill="none"
                      />
                      <path
                        id="Path_206777"
                        data-name="Path 206777"
                        d="M15,12a4,4,0,1,0-4-4A4,4,0,0,0,15,12Zm0-6a2,2,0,1,1-2,2A2.006,2.006,0,0,1,15,6Zm0,8c-2.67,0-8,1.34-8,4v2H23V18C23,15.34,17.67,14,15,14ZM9,18c.22-.72,3.31-2,6-2s5.8,1.29,6,2ZM6,15V12H9V10H6V7H4v3H1v2H4v3Z"
                        fill={isMinimizeCall ? "#fff" : "white"}
                      />
                    </g>
                  </g>
                </g>
              </svg>
            }
          />
          <Button
            id="Chat"
            onClick={() => {
              props.disclosureHost.open();
            }}
            endIcon={
              <svg
                id="Component_2281_1"
                data-name="Component 2281 – 1"
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 40 40"
              >
                <g
                  id="Group_93495"
                  data-name="Group 93495"
                  transform="translate(-10180 4581)"
                >
                  <g id="Group_93494" data-name="Group 93494">
                    <g
                      id="Ellipse_1315"
                      data-name="Ellipse 1315"
                      transform="translate(10180 -4581)"
                      fill="#fff"
                      stroke="#aaa"
                      stroke-width="1"
                      opacity="0"
                    >
                      <circle cx="20" cy="20" r="20" stroke="none" />
                      <circle cx="20" cy="20" r="19.5" fill="none" />
                    </g>
                    <g
                      id="chat_black_24dp_2_"
                      data-name="chat_black_24dp (2)"
                      transform="translate(10188 -4573)"
                    >
                      <path
                        id="Path_206778"
                        data-name="Path 206778"
                        d="M0,0H24V24H0Z"
                        fill="none"
                      />
                      <path
                        id="Path_206779"
                        data-name="Path 206779"
                        d="M4,4H20V16H5.17L4,17.17V4M4,2A2,2,0,0,0,2.01,4L2,22l4-4H20a2.006,2.006,0,0,0,2-2V4a2.006,2.006,0,0,0-2-2ZM6,12h8v2H6ZM6,9H18v2H6ZM6,6H18V8H6Z"
                        fill={isMinimizeCall ? "#fff" : "white"}
                      />
                    </g>
                  </g>
                </g>
              </svg>
            }
          />
          <Button
            id="settings"
            onClick={settingsDisclosure.open}
            endIcon={
              <svg
                id="Component_2280_1"
                data-name="Component 2280 – 1"
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 40 40"
              >
                <g
                  id="Group_93495"
                  data-name="Group 93495"
                  transform="translate(-10180 4581)"
                >
                  <g id="Group_93494" data-name="Group 93494">
                    <g
                      id="Ellipse_1315"
                      data-name="Ellipse 1315"
                      transform="translate(10180 -4581)"
                      fill={isMinimizeCall ? "#fff" : "white"}
                      stroke="#aaa"
                      stroke-width="1"
                      opacity="0"
                    >
                      <circle cx="20" cy="20" r="20" stroke="none" />
                      <circle cx="20" cy="20" r="19.5" fill="none" />
                    </g>
                    <g
                      id="settings_black_24dp"
                      transform="translate(10188 -4573)"
                    >
                      <path
                        id="Path_206780"
                        data-name="Path 206780"
                        d="M0,0H24V24H0Z"
                        fill="none"
                      />
                      <path
                        id="Path_206781"
                        data-name="Path 206781"
                        d="M19.43,12.98A7.793,7.793,0,0,0,19.5,12a7.793,7.793,0,0,0-.07-.98l2.11-1.65a.5.5,0,0,0,.12-.64l-2-3.46a.5.5,0,0,0-.44-.25.467.467,0,0,0-.17.03l-2.49,1a7.306,7.306,0,0,0-1.69-.98l-.38-2.65A.488.488,0,0,0,14,2H10a.488.488,0,0,0-.49.42L9.13,5.07a7.683,7.683,0,0,0-1.69.98l-2.49-1a.566.566,0,0,0-.18-.03.5.5,0,0,0-.43.25l-2,3.46a.493.493,0,0,0,.12.64l2.11,1.65A7.931,7.931,0,0,0,4.5,12a7.931,7.931,0,0,0,.07.98L2.46,14.63a.5.5,0,0,0-.12.64l2,3.46a.5.5,0,0,0,.44.25.467.467,0,0,0,.17-.03l2.49-1a7.306,7.306,0,0,0,1.69.98l.38,2.65A.488.488,0,0,0,10,22h4a.488.488,0,0,0,.49-.42l.38-2.65a7.683,7.683,0,0,0,1.69-.98l2.49,1a.566.566,0,0,0,.18.03.5.5,0,0,0,.43-.25l2-3.46a.5.5,0,0,0-.12-.64Zm-1.98-1.71a5.343,5.343,0,0,1,.05.73c0,.21-.02.43-.05.73l-.14,1.13.89.7,1.08.84-.7,1.21-1.27-.51-1.04-.42-.9.68a5.857,5.857,0,0,1-1.25.73l-1.06.43-.16,1.13L12.7,20H11.3l-.19-1.35-.16-1.13-1.06-.43a5.674,5.674,0,0,1-1.23-.71l-.91-.7-1.06.43-1.27.51-.7-1.21,1.08-.84.89-.7-.14-1.13c-.03-.31-.05-.54-.05-.74s.02-.43.05-.73l.14-1.13-.89-.7L4.72,8.6l.7-1.21,1.27.51,1.04.42.9-.68a5.857,5.857,0,0,1,1.25-.73l1.06-.43.16-1.13L11.3,4h1.39l.19,1.35.16,1.13,1.06.43a5.674,5.674,0,0,1,1.23.71l.91.7,1.06-.43,1.27-.51.7,1.21-1.07.85-.89.7.14,1.13ZM12,8a4,4,0,1,0,4,4A4,4,0,0,0,12,8Zm0,6a2,2,0,1,1,2-2A2.006,2.006,0,0,1,12,14Z"
                        fill={isMinimizeCall ? "#fff" : "white"}
                      />
                    </g>
                  </g>
                </g>
              </svg>
            }
          />
        </div>
      )}
    </Flex>
  );
};
export default VideoFooter;
